import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Sparkles, X, Loader2, CheckCircle, Clock, Upload, ImageIcon, Plus, Trash2, FileText } from "lucide-react";
import { FaTiktok, FaInstagram, FaYoutube, FaFacebook, FaXTwitter } from "react-icons/fa6";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type SocialPlatform = 'instagram' | 'youtube' | 'tiktok' | 'facebook' | 'twitter';

const SOCIAL_PLATFORMS: { 
  value: SocialPlatform; 
  label: string; 
  icon: React.ReactNode; 
  placeholder: string;
  followerLabel: string;
  followerLabelAr: string;
}[] = [
  { value: 'instagram', label: 'Instagram', icon: <FaInstagram className="h-5 w-5 text-pink-500" />, placeholder: 'https://instagram.com/username', followerLabel: 'Followers', followerLabelAr: 'متابعين' },
  { value: 'youtube', label: 'YouTube', icon: <FaYoutube className="h-5 w-5 text-red-500" />, placeholder: 'https://youtube.com/@channel', followerLabel: 'Subscribers', followerLabelAr: 'مشتركين' },
  { value: 'tiktok', label: 'TikTok', icon: <FaTiktok className="h-5 w-5" />, placeholder: 'https://tiktok.com/@username', followerLabel: 'Followers', followerLabelAr: 'متابعين' },
  { value: 'facebook', label: 'Facebook', icon: <FaFacebook className="h-5 w-5 text-blue-600" />, placeholder: 'https://facebook.com/page', followerLabel: 'Followers', followerLabelAr: 'متابعين' },
  { value: 'twitter', label: 'X (Twitter)', icon: <FaXTwitter className="h-5 w-5" />, placeholder: 'https://x.com/username', followerLabel: 'Followers', followerLabelAr: 'متابعين' },
];

interface Package {
  id: string;
  name: string;
  description: string;
  duration_seconds: number;
  price: number;
}

const CATEGORIES = [
  { value: "lifestyle", labelEn: "Lifestyle", labelAr: "نمط الحياة" },
  { value: "fashion", labelEn: "Fashion", labelAr: "الموضة" },
  { value: "beauty", labelEn: "Beauty", labelAr: "الجمال" },
  { value: "fitness", labelEn: "Fitness", labelAr: "اللياقة" },
  { value: "tech", labelEn: "Technology", labelAr: "التقنية" },
  { value: "food", labelEn: "Food", labelAr: "الطعام" },
  { value: "travel", labelEn: "Travel", labelAr: "السفر" },
  { value: "gaming", labelEn: "Gaming", labelAr: "الألعاب" },
  { value: "business", labelEn: "Business", labelAr: "الأعمال" },
  { value: "entertainment", labelEn: "Entertainment", labelAr: "الترفيه" },
];

const EXPERTISE_OPTIONS = [
  { value: "content_creation", labelEn: "Content Creation", labelAr: "إنشاء المحتوى" },
  { value: "brand_partnerships", labelEn: "Brand Partnerships", labelAr: "شراكات العلامات" },
  { value: "product_reviews", labelEn: "Product Reviews", labelAr: "مراجعات المنتجات" },
  { value: "sponsored_posts", labelEn: "Sponsored Posts", labelAr: "منشورات مدفوعة" },
  { value: "event_hosting", labelEn: "Event Hosting", labelAr: "استضافة الفعاليات" },
  { value: "tutorials", labelEn: "Tutorials", labelAr: "دروس تعليمية" },
  { value: "live_streaming", labelEn: "Live Streaming", labelAr: "البث المباشر" },
  { value: "storytelling", labelEn: "Storytelling", labelAr: "السرد القصصي" },
];

const InfluencerSignUp = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingLicense, setUploadingLicense] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingProfile, setExistingProfile] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    expertise: [] as string[],
    instagram: "",
    instagramFollowers: "",
    youtube: "",
    youtubeFollowers: "",
    tiktok: "",
    tiktokFollowers: "",
    facebook: "",
    facebookFollowers: "",
    twitter: "",
    twitterFollowers: "",
    email: user?.email || "",
    phone: "",
    image: "",
    mawthooqLicenseNumber: "",
    mawthooqLicenseUrl: "",
  });
  
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);

  // Check if user already has an influencer profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('influencers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        
        if (data) {
          setExistingProfile(data);
        }
      } catch (error) {
        console.error('Error checking existing profile:', error);
      } finally {
        setCheckingExisting(false);
      }
    };

    checkExistingProfile();
  }, [user]);

  const formatFollowerCount = (value: string): string => {
    // Remove existing commas and non-numeric characters except K, M, B
    const cleaned = value.replace(/,/g, '');
    
    // Check if it ends with K, M, B (shorthand notation)
    const match = cleaned.match(/^(\d+\.?\d*)\s*([KkMmBb]?)$/);
    if (match) {
      const num = match[1];
      const suffix = match[2].toUpperCase();
      // Format the numeric part with commas
      const formatted = num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return suffix ? `${formatted}${suffix}` : formatted;
    }
    
    // If just numbers, format with commas
    const numOnly = cleaned.replace(/[^\d]/g, '');
    return numOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleFollowerChange = (field: string, value: string) => {
    const formatted = formatFollowerCount(value);
    setFormData(prev => ({ ...prev, [field]: formatted }));
  };

  const handleExpertiseToggle = (value: string) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(value)
        ? prev.expertise.filter(e => e !== value)
        : [...prev.expertise, value]
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى اختيار ملف صورة" : "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingImage(true);

    try {
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('influencer-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('influencer-images')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, image: publicUrl }));

      toast({
        title: isRTL ? "تم الرفع" : "Uploaded",
        description: isRTL ? "تم رفع الصورة بنجاح" : "Image uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفع الصورة" : "Failed to upload image",
        variant: "destructive",
      });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLicenseUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type (PDF, images)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى اختيار ملف PDF أو صورة" : "Please select a PDF or image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "حجم الملف يجب أن يكون أقل من 10 ميجابايت" : "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingLicense(true);
    setLicenseFile(file);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/mawthooq-license.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('license-documents')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('license-documents')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, mawthooqLicenseUrl: publicUrl }));

      toast({
        title: isRTL ? "تم الرفع" : "Uploaded",
        description: isRTL ? "تم رفع الرخصة بنجاح" : "License uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading license:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفع الرخصة" : "Failed to upload license",
        variant: "destructive",
      });
      setLicenseFile(null);
    } finally {
      setUploadingLicense(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يجب تسجيل الدخول أولاً" : "You must be logged in first",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    // Validate packages - at least one valid package required
    const validPackages = packages.filter(p => p.name && p.price > 0);
    
    // Check if at least one social media has followers
    const hasFollowers = formData.instagramFollowers || formData.youtubeFollowers || 
                         formData.tiktokFollowers || formData.facebookFollowers || formData.twitterFollowers;
    
    // Validate Mawthooq license
    if (!formData.mawthooqLicenseNumber || !formData.mawthooqLicenseUrl) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى إدخال رقم رخصة موثوق ورفع الرخصة" : "Please enter Mawthooq license number and upload the license",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.name || !formData.category || !hasFollowers || validPackages.length === 0) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى ملء جميع الحقول المطلوبة وإضافة باقة واحدة على الأقل ومنصة تواصل واحدة" : "Please fill in all required fields, add at least one package and one social platform with followers",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Calculate total followers from all platforms
      const totalFollowers = [
        formData.instagramFollowers,
        formData.youtubeFollowers,
        formData.tiktokFollowers,
        formData.facebookFollowers,
        formData.twitterFollowers
      ].filter(Boolean).join(' / ') || '0';

      const influencerData = {
        user_id: user.id,
        name: formData.name,
        category: formData.category,
        description: formData.description || null,
        followers: totalFollowers,
        price: validPackages.length > 0 ? Math.min(...validPackages.map(p => p.price)) : 0,
        packages: validPackages.map(({ id, ...rest }) => rest),
        expertise: formData.expertise,
        social_links: {
          instagram: formData.instagram || null,
          instagram_followers: formData.instagramFollowers || null,
          youtube: formData.youtube || null,
          youtube_followers: formData.youtubeFollowers || null,
          tiktok: formData.tiktok || null,
          tiktok_followers: formData.tiktokFollowers || null,
          facebook: formData.facebook || null,
          facebook_followers: formData.facebookFollowers || null,
          twitter: formData.twitter || null,
          twitter_followers: formData.twitterFollowers || null,
        },
        contact_info: {
          email: formData.email || user.email,
          phone: formData.phone || null,
        },
        image: formData.image || null,
        mawthooq_license_number: formData.mawthooqLicenseNumber,
        mawthooq_license_url: formData.mawthooqLicenseUrl,
        approval_status: 'pending',
        featured: false,
        rating: 4.5,
        availability: 'Available now',
      };

      const { error } = await supabase
        .from('influencers')
        .insert(influencerData);

      if (error) throw error;

      toast({
        title: isRTL ? "تم التقديم بنجاح!" : "Application Submitted!",
        description: isRTL 
          ? "سيتم مراجعة طلبك وسنتواصل معك قريباً" 
          : "Your application is under review. We'll notify you soon.",
      });

      navigate('/influencers');
    } catch (error: any) {
      console.error('Error submitting influencer application:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: error.message || (isRTL ? "حدث خطأ أثناء التقديم" : "An error occurred while submitting"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <p className="mb-4">{isRTL ? "يجب تسجيل الدخول للتسجيل كمؤثر" : "You must be logged in to register as an influencer"}</p>
            <Button onClick={() => navigate('/auth')}>
              {isRTL ? "تسجيل الدخول" : "Login"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingExisting) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (existingProfile) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {existingProfile.approval_status === 'approved' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : existingProfile.approval_status === 'pending' ? (
                <Clock className="h-5 w-5 text-yellow-500" />
              ) : (
                <X className="h-5 w-5 text-red-500" />
              )}
              {isRTL ? "لديك ملف شخصي بالفعل" : "You Already Have a Profile"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">{isRTL ? "الحالة:" : "Status:"}</span>
              <Badge variant={
                existingProfile.approval_status === 'approved' ? 'default' :
                existingProfile.approval_status === 'pending' ? 'secondary' : 'destructive'
              }>
                {existingProfile.approval_status === 'approved' 
                  ? (isRTL ? "موافق عليه" : "Approved")
                  : existingProfile.approval_status === 'pending'
                  ? (isRTL ? "قيد المراجعة" : "Pending Review")
                  : (isRTL ? "مرفوض" : "Rejected")}
              </Badge>
            </div>
            {existingProfile.approval_status === 'rejected' && existingProfile.rejection_reason && (
              <p className="text-sm text-destructive">
                {isRTL ? "سبب الرفض:" : "Reason:"} {existingProfile.rejection_reason}
              </p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/influencer-dashboard')} className="w-full">
                {isRTL ? "الذهاب إلى لوحة التحكم" : "Go to Dashboard"}
              </Button>
              <Button variant="outline" onClick={() => navigate('/influencers')} className="w-full">
                {isRTL ? "العودة إلى المؤثرين" : "Back to Influencers"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`container mx-auto px-4 py-12 ${isRTL ? 'text-right' : 'text-left'}`}>
      <Button 
        variant="ghost" 
        onClick={() => navigate('/influencers')}
        className="mb-6"
      >
        <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
        {isRTL ? "العودة إلى المؤثرين" : "Back to Influencers"}
      </Button>

      <Card className="max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {isRTL ? "انضم كمؤثر" : "Join as an Influencer"}
          </CardTitle>
          <CardDescription>
            {isRTL 
              ? "أكمل ملفك الشخصي للانضمام إلى منصتنا والتواصل مع العلامات التجارية"
              : "Complete your profile to join our platform and connect with brands"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "المعلومات الأساسية" : "Basic Information"}
              </h3>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{isRTL ? "الاسم *" : "Name *"}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={isRTL ? "اسمك الكامل أو اسم العلامة" : "Your full name or brand name"}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">{isRTL ? "الفئة *" : "Category *"}</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isRTL ? "اختر الفئة" : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {isRTL ? cat.labelAr : cat.labelEn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{isRTL ? "نبذة عنك" : "About You"}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={isRTL ? "أخبرنا عن نفسك ومحتواك..." : "Tell us about yourself and your content..."}
                  rows={4}
                />
              </div>
            </div>

            {/* Packages */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "باقات التسعير *" : "Pricing Packages *"}
              </h3>
              
              <p className="text-sm text-muted-foreground">
                {isRTL 
                  ? "أضف باقاتك المخصصة مع المدة والسعر لكل واحدة" 
                  : "Add your custom packages with duration and price for each"}
              </p>
              
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm">
                <p className="text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">{isRTL ? "ملاحظة:" : "Note:"}</span>{" "}
                  {isRTL 
                    ? "ستضيف المنصة 10% على الأسعار التي تحددها. هذه هي عمولة المنصة على كل حجز." 
                    : "The platform will add 10% to the prices you specify. This is our service fee on each booking."}
                </p>
              </div>

              {packages.length === 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-dashed h-20 flex flex-col gap-1"
                  onClick={() => setPackages([{ 
                    id: crypto.randomUUID(), 
                    name: "", 
                    description: "", 
                    duration_seconds: 15, 
                    price: 0 
                  }])}
                >
                  <Plus className="h-5 w-5" />
                  <span>{isRTL ? "إضافة أول باقة" : "Add Your First Package"}</span>
                </Button>
              ) : (
                <div className="space-y-4">
                  {packages.map((pkg, index) => (
                    <Card key={pkg.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">
                            {isRTL ? `الباقة ${index + 1}` : `Package ${index + 1}`}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPackages(prev => prev.filter(p => p.id !== pkg.id))}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1">
                            <Label className="text-xs">{isRTL ? "اسم الباقة *" : "Package Name *"}</Label>
                            <Input
                              value={pkg.name}
                              onChange={(e) => setPackages(prev => prev.map(p => 
                                p.id === pkg.id ? { ...p, name: e.target.value } : p
                              ))}
                              placeholder={isRTL ? "مثال: صيحة سريعة" : "e.g., Quick Shoutout"}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">{isRTL ? "المدة (ثواني)" : "Duration (sec)"}</Label>
                              <Input
                                type="number"
                                value={pkg.duration_seconds}
                                onChange={(e) => setPackages(prev => prev.map(p => 
                                  p.id === pkg.id ? { ...p, duration_seconds: parseInt(e.target.value) || 0 } : p
                                ))}
                                min="1"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">{isRTL ? "السعر (ريال) *" : "Price (SAR) *"}</Label>
                              <Input
                                type="number"
                                value={pkg.price || ""}
                                onChange={(e) => setPackages(prev => prev.map(p => 
                                  p.id === pkg.id ? { ...p, price: parseFloat(e.target.value) || 0 } : p
                                ))}
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">{isRTL ? "الوصف" : "Description"}</Label>
                          <Input
                            value={pkg.description}
                            onChange={(e) => setPackages(prev => prev.map(p => 
                              p.id === pkg.id ? { ...p, description: e.target.value } : p
                            ))}
                            placeholder={isRTL ? "ماذا يتضمن هذه الباقة؟" : "What's included in this package?"}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPackages(prev => [...prev, { 
                      id: crypto.randomUUID(), 
                      name: "", 
                      description: "", 
                      duration_seconds: 15, 
                      price: 0 
                    }])}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {isRTL ? "إضافة باقة أخرى" : "Add Another Package"}
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label>{isRTL ? "صورة الملف الشخصي" : "Profile Image"}</Label>
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20 border-2 border-dashed border-muted-foreground/25">
                    <AvatarImage src={imagePreview || formData.image || undefined} />
                    <AvatarFallback>
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-muted-foreground/25 rounded-lg hover:bg-muted/50 transition-colors">
                        {uploadingImage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">{isRTL ? "جاري الرفع..." : "Uploading..."}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            <span className="text-sm">{isRTL ? "اختر صورة" : "Choose Image"}</span>
                          </>
                        )}
                      </div>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isRTL ? "الحد الأقصى 5 ميجابايت (JPG, PNG, WebP)" : "Max 5MB (JPG, PNG, WebP)"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Expertise */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "مجالات الخبرة" : "Areas of Expertise"}
              </h3>
              <div className="flex flex-wrap gap-2">
                {EXPERTISE_OPTIONS.map(exp => (
                  <Badge
                    key={exp.value}
                    variant={formData.expertise.includes(exp.value) ? "default" : "outline"}
                    className="cursor-pointer transition-colors"
                    onClick={() => handleExpertiseToggle(exp.value)}
                  >
                    {isRTL ? exp.labelAr : exp.labelEn}
                    {formData.expertise.includes(exp.value) && (
                      <X className="h-3 w-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Social Links with Followers */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "روابط التواصل الاجتماعي *" : "Social Media Links *"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isRTL ? "أضف منصة واحدة على الأقل مع عدد المتابعين" : "Add at least one platform with follower count"}
              </p>
              
              {/* Platform selector dropdown */}
              {selectedPlatforms.length < SOCIAL_PLATFORMS.length && (
                <Select
                  onValueChange={(value: SocialPlatform) => {
                    if (!selectedPlatforms.includes(value)) {
                      setSelectedPlatforms(prev => [...prev, value]);
                    }
                  }}
                  value=""
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={isRTL ? "اختر منصة للإضافة..." : "Select a platform to add..."} />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {SOCIAL_PLATFORMS.filter(p => !selectedPlatforms.includes(p.value)).map(platform => (
                      <SelectItem key={platform.value} value={platform.value}>
                        <div className="flex items-center gap-2">
                          {platform.icon}
                          <span>{platform.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Selected platforms */}
              <div className="space-y-4">
                {selectedPlatforms.map(platformKey => {
                  const platform = SOCIAL_PLATFORMS.find(p => p.value === platformKey)!;
                  const urlField = platformKey as keyof typeof formData;
                  const followersField = `${platformKey}Followers` as keyof typeof formData;
                  
                  return (
                    <Card key={platformKey} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {platform.icon}
                            <span className="text-sm font-medium">{platform.label}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedPlatforms(prev => prev.filter(p => p !== platformKey));
                              setFormData(prev => ({
                                ...prev,
                                [urlField]: '',
                                [followersField]: ''
                              }));
                            }}
                            className="text-destructive hover:text-destructive h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-[1fr,120px]">
                          <Input
                            value={formData[urlField] as string}
                            onChange={(e) => setFormData(prev => ({ ...prev, [urlField]: e.target.value }))}
                            placeholder={platform.placeholder}
                          />
                          <Input
                            value={formData[followersField] as string}
                            onChange={(e) => handleFollowerChange(followersField, e.target.value)}
                            placeholder={isRTL ? platform.followerLabelAr : platform.followerLabel}
                          />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              
              {selectedPlatforms.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
                  {isRTL ? "لم يتم إضافة أي منصة بعد" : "No platforms added yet"}
                </p>
              )}
            </div>

            {/* Mawthooq License Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "رخصة موثوق *" : "Mawthooq License *"}
              </h3>
              
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                <p className="text-blue-800 dark:text-blue-200">
                  {isRTL 
                    ? "يجب على جميع المؤثرين تقديم رخصة موثوق الصادرة من وزارة الإعلام السعودية للتحقق من هويتهم."
                    : "All influencers must provide a Mawthooq license issued by the Saudi Ministry of Media for identity verification."}
                </p>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="mawthooq-number">{isRTL ? "رقم الرخصة *" : "License Number *"}</Label>
                  <Input
                    id="mawthooq-number"
                    value={formData.mawthooqLicenseNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, mawthooqLicenseNumber: e.target.value }))}
                    placeholder={isRTL ? "أدخل رقم رخصة موثوق" : "Enter Mawthooq license number"}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{isRTL ? "رفع الرخصة *" : "Upload License *"}</Label>
                  <div className="flex items-center gap-2">
                    <label htmlFor="license-upload" className="cursor-pointer flex-1">
                      <div className={`flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg transition-colors ${
                        formData.mawthooqLicenseUrl 
                          ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                          : 'border-muted-foreground/25 hover:border-primary/50'
                      }`}>
                        {uploadingLicense ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">{isRTL ? "جاري الرفع..." : "Uploading..."}</span>
                          </>
                        ) : formData.mawthooqLicenseUrl ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-600">{licenseFile?.name || (isRTL ? "تم الرفع" : "Uploaded")}</span>
                          </>
                        ) : (
                          <>
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{isRTL ? "اختر ملف" : "Choose file"}</span>
                          </>
                        )}
                      </div>
                      <input
                        id="license-upload"
                        type="file"
                        accept=".pdf,image/*"
                        onChange={handleLicenseUpload}
                        className="hidden"
                        disabled={uploadingLicense}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? "PDF أو صورة، حتى 10 ميجابايت" : "PDF or image, up to 10MB"}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                {isRTL ? "معلومات الاتصال" : "Contact Information"}
              </h3>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">{isRTL ? "البريد الإلكتروني" : "Email"}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? "البريد الإلكتروني مرتبط بحسابك" : "Email is linked to your account"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{isRTL ? "رقم الهاتف" : "Phone Number"}</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+966 5XX XXX XXXX"
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isRTL ? "جاري التقديم..." : "Submitting..."}
                </>
              ) : (
                isRTL ? "تقديم الطلب" : "Submit Application"
              )}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              {isRTL 
                ? "بالتقديم، أنت توافق على مراجعة طلبك من قبل فريقنا. سيتم إعلامك بمجرد الموافقة."
                : "By submitting, you agree to have your application reviewed by our team. You'll be notified once approved."}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InfluencerSignUp;
